/**
 * Cart discount-code input.
 *
 * A checkbox-triggered disclosure that reveals a discount/gift-card field.
 * Applied codes are shown as removable chips. Used on both the cart page and
 * the cart drawer. Matches the theme convention of plain custom elements.
 */
class CartDrawerDiscountInput extends HTMLElement {
  /**
   * Initializes the component, binds events, and loads initial state.
   */
  connectedCallback() {
    this.controller = new AbortController();
    this.cacheRefs();
    this.bindEvents();
    this.loadInitialState();
    this.subscribeToCartUpdates();
  }

  /**
   * Cleans up event listeners and controllers when removed from DOM.
   */
  disconnectedCallback() {
    this.controller?.abort();
    this.cartUpdateUnsubscriber?.();
  }

  /* ---------- Cross-component sync ---------- */
  /**
   * Keep the applied-code chips in sync with the cart on every cart change —
   * not just this element's own apply/remove. Without this the pill only
   * refreshes on the element's own actions and on a full page load, so any
   * out-of-band change (quantity edit, item removal, a code becoming
   * (in)applicable, or a section re-render that resets this element) leaves a
   * stale pill until the page is reloaded.
   */
  subscribeToCartUpdates() {
    const eventName = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : 'cart-update';
    this.cartUpdateUnsubscriber = subscribe(eventName, (event) => this.handleCartUpdate(event));
  }

  /**
   * @param {{cartData?: object}} [event]
   */
  async handleCartUpdate(event) {
    try {
      // Reuse the event's cart payload when it carries discount data; otherwise
      // fetch a fresh cart so applicability is always authoritative.
      const cart =
        event?.cartData && Array.isArray(event.cartData.discount_codes) ? event.cartData : await this.fetchCart();
      this.updateUIState(cart);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error syncing discount chips on cart update:', error);
    }
  }

  /**
   * Caches DOM references for later use.
   */
  cacheRefs() {
    this.refs = {
      root: this.closest('.discount-input'),
      checkbox: this.querySelector('.discount-input__checkbox'),
      toggle: this.querySelector('.discount-input__toggle'),
      content: this.querySelector('.discount-input__content'),
      form: this.querySelector('.discount-input__form'),
      field: this.querySelector('.discount-input__field'),
      input: this.querySelector('.discount-input__input'),
      submit: this.querySelector('.discount-input__submit'),
      applied: this.querySelector('.discount-input__applied'),
      chips: this.querySelector('.discount-input__chips'),
      template: this.querySelector('.discount-input__tag-template'),
      error: this.querySelector('.discount-input__error'),
    };
  }

  /**
   * Binds event listeners to the cached DOM elements.
   */
  bindEvents() {
    const { signal } = this.controller;
    this.refs.checkbox?.addEventListener('change', () => this.handleToggle(), { signal });
    this.refs.input?.addEventListener(
      'input',
      () => {
        this.hideError();
      },
      { signal },
    );
    this.refs.form?.addEventListener('submit', (event) => this.handleFormSubmit(event), { signal });
  }

  /* ---------- Disclosure expand / collapse ---------- */
  /**
   * Toggles the disclosure expand/collapse state based on the checkbox.
   */
  handleToggle() {
    if (this.refs.checkbox?.checked) {
      this.expand();
      return;
    }
    this.collapse();
  }

  /**
   * Expands the discount input disclosure.
   */
  expand() {
    const { checkbox, toggle, content, input } = this.refs;
    if (checkbox) checkbox.checked = true;
    checkbox?.setAttribute('aria-expanded', 'true');
    if (toggle) toggle.hidden = true;
    if (content) content.hidden = false;
    input?.focus();
  }

  /**
   * Collapses the discount input disclosure and hides any errors.
   */
  collapse() {
    const { checkbox, toggle, content } = this.refs;
    if (checkbox) checkbox.checked = false;
    checkbox?.setAttribute('aria-expanded', 'false');
    if (content) content.hidden = true;
    if (toggle) toggle.hidden = false;
    this.hideError();
  }

  /* ---------- Cart state ---------- */
  /**
   * Fetches the cart and updates the initial UI state.
   */
  async loadInitialState() {
    try {
      const cart = await this.fetchCart();
      this.updateUIState(cart);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading discount initial state:', error);
    }
  }

  /**
   * @returns {Promise<object>} The current cart object.
   */
  async fetchCart() {
    const url = new URL('cart.js', `${window.location.origin}${window.Shopify.routes.root}`);
    const response = await fetch(url, { signal: this.controller.signal });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * @param {object} cart
   */
  updateUIState(cart) {
    this.discountCodes = cart.discount_codes || [];
    const applicable = this.findApplicableDiscounts(this.discountCodes);
    if (applicable.length > 0) {
      this.showChips(applicable);
      return;
    }
    this.clearChips();
  }

  /**
   * Filters a list of discount codes to only those that are applicable.
   * @param {Array<object>} discountCodes - Array of discount objects
   * @returns {Array<object>} Array of applicable discount objects
   */
  findApplicableDiscounts(discountCodes) {
    return discountCodes.filter((discount) => discount.applicable);
  }

  /* ---------- Apply ---------- */
  /**
   * Handles the discount form submission.
   * @param {Event} event - The submit event
   */
  handleFormSubmit(event) {
    event.preventDefault();
    const code = this.refs.input?.value.trim();
    if (!code) return;
    this.applyDiscountCode(code);
  }

  /**
   * @param {string} code
   */
  async applyDiscountCode(code) {
    this.setSubmitDisabled(true);
    this.hideError();

    try {
      const cart = await this.updateCartDiscount(code, 'add');
      if (!this.isDiscountApplicable(cart.discount_codes, code)) {
        this.setSubmitDisabled(false);
        this.showError();
        return;
      }

      if (this.refs.input) this.refs.input.value = '';

      this.setSubmitDisabled(false);
      this.collapse();
      this.refs.checkbox?.focus();

      this.updateUIState(cart);
      await this.refreshCart();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error applying discount code:', error);
      this.setSubmitDisabled(false);
      this.showError();
    }
  }

  /**
   * @param {string} code
   * @param {'add'|'remove'|'clear'} operation
   * @returns {Promise<object>}
   */
  async updateCartDiscount(code, operation) {
    const current = (this.discountCodes || []).map((discount) => discount.code);
    let codes = '';

    if (operation === 'add') {
      codes = [...current, code].join(',');
    } else if (operation === 'remove') {
      codes = current.filter((existing) => existing.toLowerCase() !== code.toLowerCase()).join(',');
    }

    const body = new FormData();
    body.append('discount', codes);

    const url = new URL('cart/update.js', `${window.location.origin}${window.Shopify.routes.root}`);
    const response = await fetch(url, { method: 'POST', body, signal: this.controller.signal });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Checks if a specific discount code is applicable in the cart.
   * @param {Array<object>} discountCodes - Array of discount objects
   * @param {string} code - The discount code string to check
   * @returns {boolean} True if applicable, false otherwise
   */
  isDiscountApplicable(discountCodes, code) {
    return discountCodes.some((discount) => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable);
  }

  /* ---------- Chips ---------- */
  /**
   * @param {Array<{code: string}>} applicable
   */
  showChips(applicable) {
    const { chips, applied } = this.refs;
    if (!chips) return;

    chips.innerHTML = '';
    for (const discount of applicable) {
      this.insertChip(discount.code);
    }
    applied?.classList.toggle('hidden', applicable.length === 0);
  }

  /**
   * Clears all rendered discount chips and hides the applied container.
   */
  clearChips() {
    const { chips, applied } = this.refs;
    applied?.classList.add('hidden');
    if (chips) chips.innerHTML = '';
  }

  /**
   * @param {string} code
   */
  insertChip(code) {
    const { chips, template } = this.refs;
    if (!chips || !template) return;
    
    const existingNodes = chips.querySelectorAll('.discount-input__chip-code');
    const existingCodes = Array.from(existingNodes, (node) => node.textContent || '');
    if (existingCodes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
      return;
    }

    const fragment = template.content.cloneNode(true);
    const codeEl = fragment.querySelector('.discount-input__chip-code');
    const removeButton = fragment.querySelector('.discount-input__chip-remove');

    if (codeEl) codeEl.textContent = code;
    if (removeButton) {
      const label = removeButton.querySelector('.visually-hidden')?.textContent.trim();
      if (label) removeButton.setAttribute('aria-label', `${label} ${code}`);
      removeButton.addEventListener('click', () => this.removeDiscountCode(code), {
        signal: this.controller.signal,
      });
    }

    chips.appendChild(fragment);
  }

  /**
   * @param {string} code
   */
  async removeDiscountCode(code) {
    try {
      await this.updateCartDiscount(code, 'remove');

      const tags = this.refs.chips?.querySelectorAll('.discount-input__chip') || [];
      for (const tag of tags) {
        if (tag.querySelector('.discount-input__chip-code')?.textContent === code) {
          tag.remove();
        }
      }

      const remaining = this.refs.chips?.querySelectorAll('.discount-input__chip') || [];
      if (remaining.length === 0) {
        this.refs.applied?.classList.add('hidden');
        this.moveFocusAfterRemoval();
      } else {
        remaining[remaining.length - 1].querySelector('.discount-input__chip-remove')?.focus();
      }

      await this.refreshCart();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error removing discount code:', error);
    }
  }

  /** Return focus to the disclosure trigger once the last chip is gone. */
  moveFocusAfterRemoval() {
    this.refs.checkbox?.focus();
  }

  /* ---------- Helpers ---------- */
  /**
   * Enables or disables the submit button.
   * @param {boolean} disabled - Whether the submit button should be disabled
   */
  setSubmitDisabled(disabled) {
    if (this.refs.submit) this.refs.submit.disabled = disabled;
  }

  /**
   * Shows the error message for an invalid discount code.
   */
  showError() {
    this.refs.input?.setAttribute('aria-invalid', 'true');
    this.refs.field?.classList.add('field--with-error');
    this.refs.error?.classList.remove('hidden');
  }

  /**
   * Hides the error message for an invalid discount code.
   */
  hideError() {
    this.refs.input?.removeAttribute('aria-invalid');
    this.refs.field?.classList.remove('field--with-error');
    this.refs.error?.classList.add('hidden');
  }

  /**
   * Refreshes the cart state via API and publishes an update event.
   */
  async refreshCart() {
    const eventName = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : 'cart-update';
    try {
      const cart = await this.fetchCart();
      // Authoritatively re-render the line items + totals from fresh server
      // HTML before notifying other components.
      await this.renderCartSections();
      publish(eventName, { source: 'discount-input', cartData: cart });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error refreshing cart:', error);
      publish(eventName, { source: 'discount-input', cartData: null });
    }
  }

  /**
   * Refetch the cart section and re-render the line items + totals from server
   * HTML. Line-level discount badges (and totals) are server-rendered, so after
   * a code is added or removed the cards must come from fresh markup. The
   * pub/sub partial-render path can leave a stale discount badge on a card —
   * most visibly on removal, where the badge lingers until a manual reload — so
   * we do an explicit full refetch of the cart section here. Works for both the
   * drawer (cart-drawer-items) and the cart page (cart-items); the discount
   * input itself sits outside these containers, so it is never torn down.
   */
  async renderCartSections() {
    const inDrawer = !!this.closest('cart-drawer');
    const sectionId =
      inDrawer && typeof getCartDrawerSectionId === 'function' ? getCartDrawerSectionId() : 'main-cart';

    // Render the section in the context of the CURRENT page path — NOT /cart.
    // The cart-drawer section is guarded by `request.page_type != 'cart'`, so
    // requesting it against the /cart URL returns empty markup (which is why the
    // drawer cards previously failed to refresh on discount add/remove). The
    // current path is also correct for the cart page itself (main-cart section).
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url, { signal: this.controller.signal });
    if (!response.ok) return;
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const selectors = inDrawer ? ['cart-drawer-items', '.cart-drawer__totals'] : ['cart-items', '.cart-drawer__totals'];
    for (const selector of selectors) {
      const current = document.querySelector(selector);
      const fresh = html.querySelector(selector);
      if (current && fresh) current.replaceWith(fresh);
    }
  }
}

customElements.define('cart-drawer-discount-input', CartDrawerDiscountInput);
